/**
 * Vilka tekniska attribut SKILJER produkterna i en lista åt?
 *
 * Bakgrund, uppmätt 2026-09-08: en sökning efter generator gav fem träffar som
 * alla hette "Generator", alla från JP Group, alla märkta "14V" — det enda
 * attribut som var identiskt för samtliga. Laddströmmen (110, 140 och 150 A),
 * som är precis det man väljer på, fanns i datan men visades aldrig. Katalogen
 * har dessutom 4 393 produkter som alla heter "Bromsbeläggssats, Framaxel".
 *
 * Regeln behöver ingen underhållen lista per kategori: bromsskivor får diameter,
 * generatorer ampere, filter höjd, av sig själva.
 */

interface HarAttribut {
  id_category_default?: number | null;
  category_name?: string | null;
  key_features?: Record<string, string> | null;
}

/** Kategorinyckel — jämförelsen görs inom kategorin, se nedan. */
export function kategoriNyckel(p: HarAttribut): string {
  return String(p.id_category_default ?? p.category_name ?? '—');
}

/**
 * Returnerar {kategorinyckel: [attributnamn]}, högst två per kategori.
 *
 * Jämförelsen görs PER KATEGORI, inte per lista. En resultatsida blandar
 * generatorer, regulatorer och remskivor; räknar man över alltihop vinner
 * attribut som bara finns på några få rader, och just de raderna får ingenting
 * att visa. Det är inom kategorin kunden faktiskt väljer.
 */
export function distinguishingFeatures<T extends HarAttribut>(produkter: T[]): Map<string, string[]> {
  const perKategori = new Map<string, Map<string, Set<string>>>();
  const antal = new Map<string, number>();

  for (const p of produkter) {
    const kat = kategoriNyckel(p);
    antal.set(kat, (antal.get(kat) ?? 0) + 1);
    if (!perKategori.has(kat)) perKategori.set(kat, new Map());
    const attr = perKategori.get(kat)!;
    for (const [k, v] of Object.entries(p.key_features || {})) {
      // Kvaliteten visas som badgen "Original" (lib/kvalitet.ts), inte som textrad.
      if (!v || k === 'Kvalitet') continue;
      if (!attr.has(k)) attr.set(k, new Set());
      attr.get(k)!.add(String(v));
    }
  }

  const ut = new Map<string, string[]>();
  for (const [kat, attr] of perKategori) {
    const n = antal.get(kat) ?? 0;
    if (n < 2) { ut.set(kat, []); continue; }
    ut.set(kat, [...attr.entries()]
      // Måste variera, men inte vara näst intill unikt per produkt — då är det
      // ett serienummer snarare än en egenskap man väljer på.
      .filter(([, vals]) => vals.size > 1 && vals.size <= Math.max(2, Math.ceil(n * 0.8)))
      .sort((a, b) => b[1].size - a[1].size)
      .slice(0, 2)
      .map(([k]) => k));
  }
  return ut;
}

/** "Generator-laddström [A]" + "140" → "Generator-laddström 140 A". */
export function formatFeature(namn: string, varde: string): string {
  const enhet = namn.match(/\[([^\]]+)\]/)?.[1] ?? '';
  const etikett = namn.replace(/\s*\[[^\]]*\]\s*$/, '').trim();
  return `${etikett} ${varde}${enhet ? ` ${enhet}` : ''}`;
}

/** Färdiga strängar för ett kort, tomt om inget skiljer. */
export function featureLabels(
  p: HarAttribut,
  perKategori: Map<string, string[]>
): string[] {
  const namn = perKategori.get(kategoriNyckel(p)) ?? [];
  return namn
    .map((n) => { const v = p.key_features?.[n]; return v ? formatFeature(n, v) : null; })
    .filter((x): x is string => x !== null);
}
